import React from 'react';

import camelCaseToTitleCase from 'common/util/camelCaseToTitleCase';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import makeFullName from 'common/util/makeFullName';
import getNullEmptyValue from 'common/util/getNullEmptyValue';
import { formatPrice } from 'common/formatters';

import {
  UTILITY_INVOICE_STATUS_CANCELLED,
  UTILITY_INVOICE_STATUS_PAID,
  UTILITY_INVOICE_STATUS_LIST,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER,
} from 'common/utility/constants';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
  SR_API_ACTION_UTILITY_INVOICE_UPDATE,
  SR_API_ACTION_UTILITY_PAYOUT_UPDATE,
} from 'api/utilityActionTypes';

import { showFieldIf, transformBooleanToYesNo } from 'client/helpers/formikHelpers';

import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';

export function makeAccountingDashboardRows({
  rows,
  utilityDealers,
  utilitySalesReps,
  onEditableClick,
  updateActivationItem,
  highlightRowId,
  setHighlightRowId,
}) {
  return rows.map((row) => ({
    onClick: () => {
      setHighlightRowId(row.id);
    },
    className: highlightRowId === row.id ? 'highlighted' : '',
    cells: [
      {
        dataTableFieldId: 'utilitySalesRepName',
        label: 'Sales Rep',
        editable: {
          getFormFields: () => {
            return [
              {
                title: 'Sales Rep',
                id: 'utilitySalesRepId',
                type: 'select',
                properties: {
                  placeholder: '--',
                  enableResetOption: true,
                  choices: (utilitySalesReps || []).map(({ id, firstName, lastName }) => ({
                    label: makeFullName(firstName, lastName),
                    value: id,
                  })),
                },
              },
            ];
          },
          onSubmit: (formValues) => {
            updateActivationItem({
              actionType: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
              itemId: row.utilityActivationItemId,
              payload: {
                id: row.utilityActivationId,
                updates: {
                  utilitySalesRepId: parseInt(formValues.utilitySalesRepId, 10) || null,
                },
              },
            });
          },
          defaultValues: {
            utilitySalesRepId: String(row.utilitySalesRepId),
          },
        },
      },
      {
        dataTableFieldId: 'renterName',
        label: 'Renter Name',
      },
      {
        dataTableFieldId: 'renterNumber',
        label: 'Renter Number',
      },
      {
        dataTableFieldId: 'partnerName',
        label: 'Partner',
      },
      {
        dataTableFieldId: 'addressStreetDisplay',
        label: 'Address',
      },
      {
        dataTableFieldId: 'addressStateCode',
        label: 'State',
      },
      {
        dataTableFieldId: 'accountRequestedAt',
        label: 'Purchase Date',
        value: row.accountRequestedAt
          ? momentWithAustinTimezone(row.accountRequestedAt).format('MM/DD/YYYY [at] h:mmA')
          : '--',
      },
      {
        dataTableFieldId: 'leaseStartDate',
        label: 'Move-In Date / Installation Date',
      },
      {
        dataTableFieldId: 'providerName',
        label: 'Provider',
      },
      {
        dataTableFieldId: 'providerPlanInternetSpeedMbps',
        label: 'Speed mbps',
      },
      {
        dataTableFieldId: 'providerPlanName',
        label: 'Package',
        value: `${row.providerPlanName} - ${row.utilityProviderPlanId}`,
      },
      {
        label: 'UPT Link',
        content: (
          <SrLink
            to={
              `/utility-profit/admin/processing/${row.utilityActivationShortId}` +
              `?backUrl=${encodeURIComponent('/utility-profit/admin/dashboard/accounting')}` +
              `&backTitle=${encodeURIComponent('Back to Accounting Dashboard')}`
            }
            target="_blank"
          >
            <SrText
              font="greycliff"
              color="blue300"
              cursor="pointer"
              weight="800"
              fontSize="16px"
              lineHeight="50px"
            >
              Link
            </SrText>
          </SrLink>
        ),
      },
      {
        dataTableFieldId: 'dealerName',
        label: 'Dealer',
        editable: {
          getFormFields: () => {
            const availableDealersList = utilityDealers.filter(({ id, utilityProviderPlanIds }) => {
              return (
                row.utilityDealerId === id ||
                (row.utilityProviderPlanId &&
                  utilityProviderPlanIds.includes(row.utilityProviderPlanId))
              );
            });

            return [
              {
                title: 'Dealer',
                id: 'utilityDealerId',
                type: 'select',
                properties: {
                  placeholder: !row.utilityProviderPlanId
                    ? 'No plan has been selected yet.'
                    : availableDealersList.length === 0
                    ? 'No dealer portals have been set up for this plan.'
                    : '--',
                  enableResetOption: true,
                  choices: (availableDealersList || []).map(({ id, name }) => ({
                    label: name,
                    value: id,
                  })),
                },
              },
            ];
          },
          onSubmit: (formValues) => {
            updateActivationItem({
              actionType: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
              itemId: row.utilityActivationItemId,
              payload: {
                id: row.utilityActivationItemId,
                updates: formValues,
              },
            });
          },
          defaultValues: {
            utilityDealerId: row.utilityDealerId,
          },
        },
      },
      {
        dataTableFieldId: 'utilityDealerExternalPurchaseId',
        label: 'Purchase ID',
        editable: 'short_text',
      },
      {
        dataTableFieldId: 'commissionAmount',
        label: 'Expected Revenue to UP',
        value: typeof row.commissionAmount === 'number' ? formatPrice(row.commissionAmount) : '--',
      },
      {
        dataTableFieldId: 'invoiceAmount',
        label: 'Actual Revenue to UP',
        value: typeof row.invoiceAmount === 'number' ? formatPrice(row.invoiceAmount) : '--',
        editable: !row.invoiceId
          ? null
          : {
              getFormFields: () => [
                {
                  id: 'amount',
                  label: 'Invoice Amount',
                  type: 'currency',
                  validations: ['required'],
                },
              ],
              onSubmit: (formValues) => {
                updateActivationItem({
                  actionType: SR_API_ACTION_UTILITY_INVOICE_UPDATE,
                  itemId: row.utilityActivationItemId,
                  payload: {
                    id: row.invoiceId,
                    updates: formValues,
                  },
                });
              },
              defaultValues: {
                amount: row.invoiceAmount,
              },
            },
      },
      {
        dataTableFieldId: 'payoutAmount',
        label: 'Payout to Partner',
        value: typeof row.payoutAmount === 'number' ? formatPrice(row.payoutAmount) : '--',
        editable: !row.payoutId
          ? null
          : {
              getFormFields: () => [
                {
                  id: 'amount',
                  label: 'Payout Amount',
                  type: 'currency',
                  validations: ['required'],
                },
              ],
              onSubmit: (formValues) => {
                updateActivationItem({
                  actionType: SR_API_ACTION_UTILITY_PAYOUT_UPDATE,
                  itemId: row.utilityActivationItemId,
                  payload: {
                    id: row.payoutId,
                    updates: formValues,
                  },
                });
              },
              defaultValues: {
                amount: row.payoutAmount,
              },
            },
      },
      {
        dataTableFieldId: 'activationStatus',
        label: 'Activation Status',
        value: camelCaseToTitleCase(row.activationStatus),
      },
      {
        dataTableFieldId: 'activationCancelReason',
        label: 'Activation Cancel Reason',
        value: camelCaseToTitleCase(row.activationCancelReason),
      },
      {
        dataTableFieldId: 'activationItemStatus',
        label: 'Purchase Request Status',
        value: camelCaseToTitleCase(row.activationItemStatus),
      },
      {
        dataTableFieldId: 'dropoutReason',
        label: 'Renter Cancelled',
        value: camelCaseToTitleCase(row.dropoutReason) || '--',
        editable: {
          getFormFields: (formValues) => [
            {
              title: 'Cancel reason?',
              id: 'dropoutReason',
              type: 'select',
              properties: {
                placeholder: 'Not Cancelled',
                enableResetOption: true,
                choices: UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST.map((value) => ({
                  label: camelCaseToTitleCase(value),
                  value,
                })),
              },
            },
            showFieldIf(formValues?.dropoutReason, {
              title:
                formValues?.dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER
                  ? 'Other reason'
                  : `"${camelCaseToTitleCase(formValues?.dropoutReason)}" explanation`,
              id: 'dropoutReasonExplanation',
              type: 'long_text',
              properties: {
                placeholder: 'Write here...',
              },
              validations:
                formValues?.dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER
                  ? ['required']
                  : [],
            }),
          ],
          onSubmit: (formValues) => {
            updateActivationItem({
              actionType: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
              itemId: row.utilityActivationItemId,
              payload: {
                id: row.utilityActivationItemId,
                updates: formValues,
              },
            });
          },
          defaultValues: {
            dropoutReason: row.dropoutReason,
            dropoutReasonExplanation: row.dropoutReasonExplanation,
          },
        },
      },
      {
        dataTableFieldId: 'isPaidByIsp',
        label: 'Paid by ISP?',
        value:
          row.invoiceStatus === UTILITY_INVOICE_STATUS_CANCELLED
            ? 'Cancelled'
            : row.invoiceStatus === UTILITY_INVOICE_STATUS_PAID
            ? 'Yes'
            : 'No',
        editable: !row.invoiceId
          ? null
          : {
              getFormFields: (formValues) => [
                {
                  id: 'status',
                  label: 'Invoice Status',
                  type: 'select',
                  properties: {
                    choices: UTILITY_INVOICE_STATUS_LIST.map((status) => ({
                      label: camelCaseToTitleCase(status),
                      value: status,
                    })),
                  },
                  validations: ['required'],
                },
                showFieldIf(formValues?.status === UTILITY_INVOICE_STATUS_CANCELLED, {
                  title: 'Cancel Reason',
                  id: 'cancelReason',
                  type: 'long_text',
                  properties: {
                    placeholder: 'Write here...',
                  },
                  validations: ['required'],
                }),
              ],
              onSubmit: (formValues) => {
                updateActivationItem({
                  actionType: SR_API_ACTION_UTILITY_INVOICE_UPDATE,
                  itemId: row.utilityActivationItemId,
                  payload: {
                    id: row.invoiceId,
                    updates: {
                      status: formValues.status,
                      cancelReason:
                        formValues.status === UTILITY_INVOICE_STATUS_CANCELLED
                          ? formValues.cancelReason
                          : null,
                    },
                  },
                });
              },
              defaultValues: {
                status: row.invoiceStatus,
                cancelReason: row.invoiceCancelReason,
              },
            },
      },
      {
        dataTableFieldId: 'isPartnerPaid',
        label: 'Paid Partner?',
      },
    ].map((cell) => {
      const rawValue = 'value' in cell ? cell.value : row[cell.dataTableFieldId];
      const editable = !cell.editable
        ? null
        : typeof cell.editable !== 'string'
        ? cell.editable
        : {
            getFormFields: () => [
              {
                id: cell.dataTableFieldId,
                label: cell.label,
                type: cell.editable,
                validations: ['required'],
              },
            ],
            onSubmit: (formValues) => {
              updateActivationItem({
                actionType: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
                itemId: row.utilityActivationItemId,
                payload: {
                  id: row.utilityActivationItemId,
                  updates: formValues,
                },
              });
            },
            defaultValues: {
              [cell.dataTableFieldId]: row[cell.dataTableFieldId],
            },
          };

      const onClick = editable ? () => onEditableClick(editable) : null;
      let content = cell.content
        ? cell.content
        : typeof rawValue === 'boolean'
        ? transformBooleanToYesNo(rawValue, true)
        : getNullEmptyValue(rawValue, '--');

      if (onClick) {
        cell.className = 'clickable';
        content = (
          <SrText underlineOnHover font="greycliff" size="16" weight="800">
            {content}
          </SrText>
        );
      }

      return {
        ...cell,
        content,
        onClick,
      };
    }),
  }));
}

const emptyRowCells = makeAccountingDashboardRows({ rows: [{}] })[0].cells;

export const accountingDashboardHeaders = emptyRowCells.map(({ dataTableFieldId, label }) => ({
  dataTableFieldId: dataTableFieldId,
  content: label,
}));
